import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Download, ExternalLink, Save } from "lucide-react";
import { ApiError, PUBLIC_BASE_URL, api, type CvDocument } from "../lib/api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Input, Textarea } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Badge } from "../components/ui/badge.js";

/** Verified CV editor: metadata, publish + searchability, skills preview, PDF (M6). */
export function CvEditPage() {
  const { id = "" } = useParams();
  const [cv, setCv] = useState<CvDocument | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [headline, setHeadline] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfMsg, setPdfMsg] = useState<string | null>(null);

  function apply(doc: CvDocument) {
    setCv(doc);
    setTitle(doc.title);
    setSlug(doc.slug);
    setHeadline(doc.sections.headline ?? "");
    setSummary(doc.sections.summary ?? "");
  }
  useEffect(() => {
    void api.getCv(id).then((r) => apply(r.cv));
  }, [id]);

  async function save(patch: Parameters<typeof api.updateCv>[1]) {
    setError(null);
    setBusy(true);
    try {
      const r = await api.updateCv(id, patch);
      apply(r.cv);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveDetails() {
    if (!cv) return;
    await save({
      title,
      slug,
      sections: { ...cv.sections, headline: headline.trim() || null, summary: summary.trim() || null },
    });
  }

  async function exportPdf() {
    setPdfMsg(null);
    try {
      const r = await api.requestCvPdf(id);
      setPdfMsg(`PDF ${r.status} — it will be available shortly.`);
    } catch (err) {
      setPdfMsg(
        err instanceof ApiError && err.status === 501
          ? "PDF export isn't enabled in this environment yet."
          : err instanceof ApiError
            ? err.message
            : "PDF request failed",
      );
    }
  }

  if (!cv) return <div className="h-64 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />;

  const published = Boolean(cv.publishedAt);
  const hostedUrl = `${PUBLIC_BASE_URL}/cv/${cv.slug}`;
  const skills = cv.sections.skills;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/cv">
            <ArrowLeft size={16} /> Back
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{cv.title}</h1>
        <Badge variant={published ? "verified" : "neutral"}>{published ? "Published" : "Draft"}</Badge>
        <div className="ml-auto flex gap-2">
          {published && (
            <Button asChild variant="secondary" size="sm">
              <a href={hostedUrl} target="_blank" rel="noopener">
                <ExternalLink size={14} /> View
              </a>
            </Button>
          )}
          <Button size="sm" onClick={() => void saveDetails()} disabled={busy}>
            <Save size={14} /> Save
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>URL slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
          <div>
            <Label>Headline</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Summary</Label>
            <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Skills — claimed vs verified</CardTitle>
        </CardHeader>
        <CardContent>
          {skills.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No skills yet. Generate this CV from a parsed resume to pull in matched skills.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {skills.map((sk, i) =>
                sk.verified ? (
                  <Badge key={i} variant="verified">
                    <BadgeCheck size={12} /> {sk.label}
                  </Badge>
                ) : (
                  <Badge key={i} variant="neutral">
                    {sk.label}
                  </Badge>
                ),
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Publishing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={published}
              onChange={(e) => void save({ published: e.target.checked })}
              disabled={busy}
            />
            <span>
              <span className="font-medium">Publish CV</span>
              <span className="block text-sm text-muted-foreground">
                Makes your CV public at <span className="font-mono">{hostedUrl}</span>.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={cv.isSearchableByEmployers}
              onChange={(e) => void save({ isSearchableByEmployers: e.target.checked })}
              disabled={busy}
            />
            <span>
              <span className="font-medium">Discoverable by employers</span>
              <span className="block text-sm text-muted-foreground">
                Opt in to appear in employer searches. Off by default — your CV stays private to people you share
                the link with. You can turn this off anytime.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>PDF export</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Export a PDF with a QR code and verify links back to your credentials. Requires the CV to be published.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => void exportPdf()} disabled={!published}>
              <Download size={14} /> Generate PDF
            </Button>
            {cv.pdfKey && (
              <Button asChild variant="ghost" size="sm">
                <a href={api.cvPdfUrl(cv.id)} target="_blank" rel="noopener">
                  <Download size={14} /> Download last PDF
                </a>
              </Button>
            )}
          </div>
          {pdfMsg && <p className="text-sm text-muted-foreground">{pdfMsg}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
